import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * Independent Stays plan IS5: the team (co-hosts and property managers) and the
 * co-host's own view (BE src/stays/routes/team.js, src/stays/routes/cohost.js).
 *
 *   GET    /stays/team                                  stays:read + user:read
 *   PUT    /stays/team/cohosts/:userId/listings         stays:manage + invite:create  {listing_ids}
 *   DELETE /stays/team/cohosts/:userId/listings         stays:manage + invite:create
 *   GET    /stays/cohost/listings                       stays:guest_cards
 *   POST   /dashboard/invitations                       invite:create (role stays_cohost + stays_listing_ids)
 */

export type TeamRole = 'stays_cohost' | 'property_manager';

export interface TeamMember {
  user_id: string;
  name: string;
  email: string;
  role: TeamRole;
  status: string;
  /** A co-host's listings (empty for a property manager). */
  listing_ids: string[];
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: TeamRole;
  listing_ids: string[];
  expires_at: string | null;
  status: string;
}

export interface TeamResponse {
  members: TeamMember[];
  invitations: TeamInvitation[];
}

export interface TeamListing {
  listing_id: string;
  display_name: string;
  building_name: string;
  status: string;
}

export interface CohostInviteInput {
  first_name: string;
  last_name: string;
  email: string;
  role: TeamRole;
  /** role stays_cohost: the listings shared with them. */
  stays_listing_ids?: string[];
  /** role property_manager: the properties they look after. */
  property_ids?: number[];
}

const wrap = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const cohostListingsPath = (userId: string) =>
  `${apiRoute}/stays/team/cohosts/${encodeURIComponent(userId)}/listings`;

export const getTeam = (): Promise<TeamResponse> => wrap(axios.get(`${apiRoute}/stays/team`), 'Could not load your team.');

/** The live listings the caller may share (or, for a co-host, work on). */
export const listTeamListings = (): Promise<{ items: TeamListing[] }> =>
  wrap(axios.get(`${apiRoute}/stays/cohost/listings`), 'Could not load the listings.');

export const setCohostListings = (userId: string, listingIds: string[]): Promise<{ member: TeamMember | null }> =>
  wrap(axios.put(cohostListingsPath(userId), { listing_ids: listingIds }), "Could not save the co-host's listings.");

export const removeCohostListings = (userId: string): Promise<void> =>
  wrap(axios.delete(cohostListingsPath(userId)), "Could not remove the co-host's listings.").then(() => undefined);

export const inviteTeamMember = (input: CohostInviteInput): Promise<{ invite: { id: string } }> =>
  wrap(axios.post(`${apiRoute}/dashboard/invitations`, input), 'Could not send the invitation.');

export const revokeTeamInvitation = (id: string): Promise<void> =>
  wrap(axios.post(`${apiRoute}/dashboard/invitations/${encodeURIComponent(id)}/revoke`, {}), 'Could not cancel the invitation.').then(
    () => undefined
  );
